// client/src/hooks/useTheme.js
import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useAuth } from './useAuth.js';
import authService from '../services/authService.js';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'fairytale-theme';

// Initial theme for first paint, BEFORE we know whether someone is logged in.
//
// Priority:
//   1. localStorage value if present and valid → respects what the visitor
//      last chose on this browser (covers logged-out visitors and avoids a
//      flash for logged-in users whose DB preference will load a moment later).
//   2. Otherwise, hard default to 'light'. We deliberately do NOT consult
//      window.matchMedia('prefers-color-scheme') here — the product
//      requirement is that first-time visitors see light mode regardless of
//      their OS setting. They can toggle to dark and we'll remember it.
function getInitialTheme() {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  }
  return 'light';
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setTheme] = useState(getInitialTheme);

  // Tracks which user's preference we've already applied. Without this we'd
  // re-overwrite local state on every AuthContext re-render (e.g. after a
  // profile update), which would clobber an in-flight toggle the user just
  // made. Compare by userid because the user object identity can change
  // even when it's the same person.
  const appliedForUserRef = useRef(null);

  // DB-wins-on-login: when the user object first arrives (login or page
  // refresh that restores the session), trust whatever's in the database
  // and overwrite local state. localStorage gets updated by the effect
  // below, keeping everything in sync.
  //
  // For logged-out users we don't touch anything — getInitialTheme already
  // handled them, and any toggles they make stay local.
  useEffect(() => {
    if (!user) {
      // User logged out (or was never logged in). Reset our "applied for"
      // marker so a future login will re-apply the DB value cleanly.
      appliedForUserRef.current = null;
      return;
    }

    if (appliedForUserRef.current === user.userid) return;

    const dbTheme = user.prefers_dark_mode ? 'dark' : 'light';
    setTheme(dbTheme);
    appliedForUserRef.current = user.userid;
  }, [user]);

  // Apply the theme to the DOM and mirror it to localStorage on every
  // change. This runs for both logged-in and logged-out users — local
  // storage is always the source of truth for the *current browser*; the
  // database is the source of truth for the *user's account*.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // toggleTheme is what the navbar button calls. Always update local state
  // (UI must respond instantly), then if a user is logged in, fire-and-forget
  // a request to persist it. We deliberately don't await — a slow or failed
  // network round-trip should never block the toggle from feeling snappy.
  // If the request fails, the user still sees their chosen theme this session;
  // worst case it doesn't sync to other devices until they toggle again.
  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next = current === 'dark' ? 'light' : 'dark';

      if (user) {
        authService
          .updateThemePreference(next === 'dark')
          .catch((err) => {
            // Non-fatal: log for debugging, don't disrupt the UI.
            console.warn('Failed to save theme preference to server:', err);
          });
      }

      return next;
    });
  }, [user]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}